import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export interface MoveFoldersBetweenPropertiesRequest {
  folder_ids: number[];
  source_property_location_id: number;
  target_property_location_id: number;
}

export interface MoveFoldersBetweenPropertiesResponse {
  success: boolean;
  message: string;
  moved_folders_count: number;
  moved_documents_count: number;
  updated_calendar_events_count: number;
}

const METHOD = 'POST';
const ENDPOINT = 'document/move-folders-between-properties';
export const QUERY_KEY = () => getQueryKey(METHOD, ENDPOINT, {});

export const useMoveFoldersBetweenProperties = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<
    MoveFoldersBetweenPropertiesResponse,
    Error,
    MoveFoldersBetweenPropertiesRequest
  >({
    mutationFn: async (request: MoveFoldersBetweenPropertiesRequest) => {
      try {
        const response = await handleApiCall<
          MoveFoldersBetweenPropertiesResponse,
          MoveFoldersBetweenPropertiesRequest
        >(session, METHOD, getResolvedEndpoint(ENDPOINT, {}), request);
        return response;
      } catch (err) {
        enqueueSnackbar('Failed to move folders between properties.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};
